"use client";

import { useState } from "react";
import { cn } from "@/lib/cn";

const sizeMap = {
  xs: "size-7 text-3xs",
  sm: "size-9 text-xs",
  md: "size-11 text-sm",
  lg: "size-14 text-base",
  xl: "size-20 text-2xl",
} as const;

export type AvatarSize = keyof typeof sizeMap;

export interface AvatarProps {
  src?: string | null;
  alt?: string;
  /** Used to derive initials + a deterministic gradient when no image. */
  name?: string;
  size?: AvatarSize;
  /**
   * Optional placeholder node rendered beneath the image (e.g. a decoded
   * blurhash canvas). Shown until the image finishes loading.
   */
  placeholder?: React.ReactNode;
  ring?: boolean;
  className?: string;
}

function initials(name?: string) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic brand-hued gradient so a given name always looks the same.
const gradients = [
  "from-flamingo to-magenta",
  "from-violet to-flamingo",
  "from-coral to-magenta",
  "from-flamingo-light to-violet",
  "from-magenta to-violet",
];

function gradientFor(name?: string) {
  if (!name) return gradients[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return gradients[Math.abs(hash) % gradients.length];
}

export function Avatar({
  src,
  alt,
  name,
  size = "md",
  placeholder,
  ring = false,
  className,
}: AvatarProps) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const showImage = src && !failed;

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        "bg-linear-to-br font-semibold text-white/95 select-none",
        gradientFor(name),
        ring &&
          "ring-2 ring-flamingo/50 ring-offset-2 ring-offset-ink shadow-glow-sm",
        sizeMap[size],
        className,
      )}
      aria-label={alt ?? name}
    >
      {/* initials sit underneath so they show while/if the image is absent */}
      <span aria-hidden={Boolean(showImage && loaded)}>{initials(name)}</span>

      {placeholder && !loaded && showImage && (
        <span className="absolute inset-0">{placeholder}</span>
      )}

      {showImage && (
        /* Avatars are small, remote, and dynamically sized; next/image adds
           no value here. */
        /* eslint-disable-next-line @next/next/no-img-element */
        <img
          src={src}
          alt={alt ?? name ?? ""}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={cn(
            "absolute inset-0 size-full object-cover transition-opacity duration-500",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </span>
  );
}
